import React, { useEffect, useState } from "react";
import { LIVELLI_OBBLIGO, livelloObbligo } from "../utils/obbligoPresenze";
import { dataStr } from "../utils/periodoLavorativo";
import { richiediPiva } from "../components/PivaObbligatoria";
import { doc, onSnapshot, updateDoc, serverTimestamp, collection, query, where, setDoc , getDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { db } from "../firebase";
import LogEmailWidget from "../components/LogEmailWidget";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Settings, Building2, FileText, Users } from "lucide-react";
import { RUOLI_OPERATIVI, PERMESSI_DEFAULT_PER_RUOLO } from "../utils/permessiDefaultPerRuolo";
import { SEZIONI } from "./AbilitazioniPage";

function ResponsabiliPresenze({ orgId, canEdit }) {
  const [utenti, setUtenti] = useState([]);
  const [autorizzati, setAutorizzati] = useState([]);
  useEffect(()=>{
    if(!orgId) return;
    const u = onSnapshot(query(collection(db,"users"),where("organizationId","==",orgId),where("enabled","==",true)), snap=>setUtenti(snap.docs.map(d=>d.data())));
    const a = onSnapshot(doc(db,"presenzeApprovatori",orgId), snap=>setAutorizzati(snap.exists()?(snap.data().uids||[]):[]));
    return ()=>{u();a();};
  },[orgId]);

  const toggle = async uid => {
    const nuovi = autorizzati.includes(uid) ? autorizzati.filter(x=>x!==uid) : [...autorizzati,uid];
    await setDoc(doc(db,"presenzeApprovatori",orgId), { uids:nuovi, organizationId:orgId }, { merge:true });
  };

  return (
    <div style={{ maxHeight:200, overflowY:"auto" }}>
      {utenti.map(u=>(
        <label key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:6, cursor: canEdit?"pointer":"default", fontSize:13, borderBottom:"1px solid #f3f4f6" }}>
          <input type="checkbox" checked={autorizzati.includes(u.uid)} onChange={()=>canEdit&&toggle(u.uid)} disabled={!canEdit}/>
          <span style={{ fontWeight:600 }}>{u.displayName}</span>
          <span style={{ fontSize:11, color:"#9ca3af" }}>{u.role==="owner"||u.isPlatformOwner?"Owner":u.role==="admin"?"Admin":"Dipendente"}</span>
        </label>
      ))}
    </div>
  );
}

// Stesso pattern di ResponsabiliPresenze, generico per riuso su altri ruoli (Magazziniere, Ufficio Acquisti)
// Sicurezza di cantiere (ott 2026): responsabile predefinito e voci aziendali della checklist
// (components/SicurezzaCantiere.js). Si salvano con il pulsante generale delle impostazioni.
function ImpostazioniSicurezza({ orgId, form, upd, inp }) {
  const [utenti, setUtenti] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"users"),where("organizationId","==",orgId),where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d=>d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"))), () => {});
  }, [orgId]);
  const voci = Array.isArray(form.sicurezzaVociAzienda) ? form.sicurezzaVociAzienda : [];
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginTop: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>🦺 Sicurezza di cantiere</h3>
      <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
        L'addetto alla sicurezza predefinito viene proposto quando una commessa è un cantiere (si può cambiare commessa per commessa) e riceve notifica ed email.
        Le voci aziendali si aggiungono alla checklist di legge di ogni cantiere.
      </p>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Addetto alla sicurezza e alla documentazione di cantiere (predefinito)</label>
      <select style={{ ...inp, maxWidth: 320, marginBottom: 14 }} value={form.responsabileSicurezzaUid || ""} onChange={e => upd("responsabileSicurezzaUid", e.target.value)}>
        <option value="">— Nessuno —</option>
        {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>)}
      </select>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Voci aziendali della checklist</label>
      {voci.map((v, i) => (
        <div key={v.id} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <input style={{ ...inp, flex: 1 }} value={v.titolo} placeholder="es. Verbale di consegna del cantiere firmato"
            onChange={e => upd("sicurezzaVociAzienda", voci.map((x, j) => j === i ? { ...x, titolo: e.target.value } : x))}/>
          <button type="button" title="Togli" onClick={() => upd("sicurezzaVociAzienda", voci.filter((_, j) => j !== i))}
            style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      ))}
      <button type="button" onClick={() => upd("sicurezzaVociAzienda", [...voci, { id: "v" + Date.now(), titolo: "" }])}
        style={{ fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 7, border: "1px dashed #7c3aed", background: "#fff", color: "#7c3aed", cursor: "pointer" }}>+ Aggiungi voce</button>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>Ricordati di salvare le impostazioni.</div>
    </div>
  );
}

export default function ImpostazioniOrgPage() {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [congelato, setCongelato] = React.useState(null);

  React.useEffect(()=>{
    if(!effectiveOrgId) return;
    // Cerca se esiste un congelato globale (non per commessa specifica — qui è globale org)
    // Mostriamo solo info — il congela per commessa è nel ControlloEconomico
  },[effectiveOrgId]);
  const canEdit = isOrgAdmin || isPlatformOwner;

  // Stampa/PDF della tabella ruoli — stesso pattern già usato per SAL e Relazione Finale
  // (finestra dedicata + window.print()). Le righe sono generate dalla stessa fonte del
  // rendering a schermo (SEZIONI, RUOLI_OPERATIVI, PERMESSI_DEFAULT_PER_RUOLO), mai scritte
  // a mano — così il PDF non può mai disallinearsi da quello che si vede a schermo.
  const stampaTabellaRuoli = () => {
    const win = window.open("", "_blank");
    const coloreDoc = form.coloreDocumenti || "#1e1b4b";
    const nomeAzienda = form.nome || form.name || "";

    const righeHtml = SEZIONI.map((s, i) => {
      const celle = RUOLI_OPERATIVI.map(r =>
        `<td style="text-align:center;">${PERMESSI_DEFAULT_PER_RUOLO[r.id]?.sezioni.includes(s.id) ? "✅" : ""}</td>`
      ).join("");
      return `<tr style="background:${i % 2 === 0 ? "#fff" : "#fafafa"};page-break-inside:avoid;">
        <td style="font-weight:600;">${s.label}</td>${celle}
      </tr>`;
    }).join("");

    const legendaHtml = RUOLI_OPERATIVI.map(r =>
      `<div style="margin-bottom:6px;"><strong style="color:${coloreDoc};">${r.label}</strong> — ${r.descrizione}</div>`
    ).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Ruoli e Permessi — ${nomeAzienda}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:10.5px;color:#111;padding:12mm 10mm 18mm;}
      @media screen{body{max-width:1200px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;margin:12mm 10mm 18mm;
        @bottom-center{content:"Pagina " counter(page) " di " counter(pages);font-size:9px;color:#9ca3af;font-family:Arial,sans-serif;}}
      @media print{.no-print{display:none;}}
      table{width:100%;border-collapse:collapse;margin-top:14px;}
      th{background:${coloreDoc};color:#fff;padding:6px 8px;font-size:9px;text-align:center;text-transform:uppercase;}
      th:first-child{text-align:left;}
      td{padding:5px 8px;border-bottom:1px solid #f3f4f6;font-size:10px;}
      thead{display:table-header-group;}
    </style></head><body>
      <div style="text-align:center;margin-bottom:4px;">
        <div style="font-size:18px;font-weight:900;color:${coloreDoc};">${nomeAzienda}</div>
        <div style="font-size:13px;color:#6b7280;margin-top:2px;">Ruoli e Permessi — cosa vede di default ciascun ruolo</div>
        <div style="font-size:9px;color:#9ca3af;margin-top:2px;">Il Titolare vede sempre tutto, non compare in tabella. Personalizzabile per ruolo o per singola persona in Abilitazioni.</div>
      </div>
      <table>
        <thead><tr><th>Sezione</th>${RUOLI_OPERATIVI.map(r => `<th>${r.label}</th>`).join("")}</tr></thead>
        <tbody>${righeHtml}</tbody>
      </table>
      <div style="margin-top:20px;font-size:10px;color:#374151;page-break-inside:avoid;">
        <div style="font-weight:700;margin-bottom:8px;color:${coloreDoc};">Cosa fa ciascun ruolo</div>
        ${legendaHtml}
      </div>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  const [form, setForm] = useState({
    nome: "", indirizzo: "", piva: "", telefono: "", email: "",
    logo: "", fontOreEconomico: "", tolleranzaRitardo: 10, orarioInizioPrevisto: "08:00", costoOrarioBase: "", oreLavorativeStandard: 8,
    oraInizioNotturno: "22:00", oraFineNotturno: "06:00",
    markupDefault: 15, costiGeneraliDefault: 10, costoKmDefault: 0.30,
    utileEODefault: 15, speseGeneraliEODefault: 10, utileSpeseViveEODefault: 5,
    disponibilitaMezziAbilitato: false,
    rapportiniOrePrecedenti: "durata",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState("identita"); // "identita" | "economico" | "presenze"

  const sezioniStato = {
    identita: !!(form.nome && form.piva),
    economico: !!form.fontOreEconomico,
    presenze: true, // ha sempre valori di default sensati
  };
  const NOMI_SEZIONE = { identita: "Identità aziendale", economico: "Configurazione economica", presenze: "Presenze e approvazioni" };
  function AvvisoRuoliOperativi() {
    return (
      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:15, flexShrink:0 }}>💜</span>
        <span style={{ fontSize:12, color:"#5b21b6" }}>
          Prima di invitare persone, dai un'occhiata al tab <strong>Ruoli Operativi</strong> — mostra cosa vede ciascun ruolo di default, utile per capire subito chi abilitare a cosa.
        </span>
      </div>
    );
  }

  function PromemoriaAltreSez({ tab }) {
    const mancanti = Object.keys(sezioniStato).filter(k => k !== tab && !sezioniStato[k]);
    if (mancanti.length === 0) return null;
    return (
      <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:15, flexShrink:0 }}>ℹ️</span>
        <span style={{ fontSize:12, color:"#92400e" }}>
          {mancanti.length===1
            ? <>Manca ancora da configurare: <strong>{NOMI_SEZIONE[mancanti[0]]}</strong>.</>
            : <>Mancano ancora da configurare: <strong>{mancanti.map(m=>NOMI_SEZIONE[m]).join(", ")}</strong>.</>}
        </span>
      </div>
    );
  }

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(doc(db, "organizations", effectiveOrgId), snap => {
      if (snap.exists()) {
        const dati = snap.data();
        // Bug reale corretto oggi: la creazione di una nuova organizzazione (sia da qui, sia
        // dalla registrazione pubblica) scrive solo il campo "name", mai "nome" — ma questo
        // modulo lavora internamente su "nome". Senza questa riga, form.nome restava vuoto per
        // ogni organizzazione più recente, e il primo salvataggio di QUALUNQUE impostazione
        // (anche solo la Fonte Ore) riscriveva "name" con una stringa vuota, cancellando il
        // nome vero dell'azienda in silenzio — è quello che è successo a Montecorvo Srl.
        setForm(f => ({ ...f, ...dati, nome: dati.nome || dati.name || "" }));
      }
    });
  }, [effectiveOrgId]);

  const uploadLogo = async (file) => {
    if (!file || !effectiveOrgId) return;
    setUploadingLogo(true);
    try {
      const storageRef = ref(storage, `logos/${effectiveOrgId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      upd("logo", url);
      addToast("Logo caricato ✅", "success");
    } catch(e) {
      addToast(messaggioErrore(e, "caricare il logo"), "error");
    }
    setUploadingLogo(false);
  };

  const salva = async () => {
    setSaving(true);
    try {
      // La P.IVA non passa da qui: la inserisce il titolare una volta sola dalla finestra
      // dedicata (controllo di validità e dei doppioni sul server) e poi la cambia solo il
      // Platform Owner (utils/pivaOrganizzazione.js).
      const { piva, ...resto } = form;
      await updateDoc(doc(db, "organizations", effectiveOrgId), { ...resto, ...(isPlatformOwner ? { piva: String(piva || "").trim() } : {}), name: form.nome, updatedAt: serverTimestamp() });
      addToast("Impostazioni salvate ✅", "success");
    } catch(e) { addToast(messaggioErrore(e), "error"); }
    setSaving(false);
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ background: "#3C3489", padding: "14px 24px", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, color: "#fff", margin: 0 }}>
          <Settings size={17} color="#fff" /> Impostazioni Organizzazione
        </h2>
        <p style={{ fontSize: 12, color: "#CECBF6", margin: "4px 0 0" }}>
          Compila tutte e tre le sezioni sotto per il corretto funzionamento della piattaforma.
        </p>
      </div>

      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 24px", flexShrink: 0, display: "flex", gap: 6 }}>
        {[
          { id: "identita", label: "Identità aziendale" },
          { id: "economico", label: "Configurazione economica" },
          { id: "presenze", label: "Presenze e approvazioni" },
          { id: "ruoli", label: "Ruoli Operativi" },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: "12px 14px", border: "none", borderBottom: `2px solid ${activeTab === t.id ? "#7c3aed" : "transparent"}`,
              background: "none", color: activeTab === t.id ? "#7c3aed" : "#6b7280", fontSize: 13, fontWeight: activeTab === t.id ? 700 : 500,
              cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {activeTab === "identita" && (<>
        <PromemoriaAltreSez tab="identita"/>
        <AvvisoRuoliOperativi/>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        <div>
        {/* Dati aziendali */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 16, color: "#374151" }}>
            <Building2 size={14} color="#7c3aed" /> Dati aziendali
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Questi dati appaiono nei PDF dei rapportini di intervento.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Nome / Ragione sociale *</label>
              <input style={inp} value={form.nome} onChange={e => upd("nome", e.target.value)} disabled={!canEdit} placeholder="Es. Rossi Impianti Srl" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>P.IVA *</label>
              {isPlatformOwner || form.piva
                ? <input style={{ ...inp, background: isPlatformOwner ? "#fff" : "#f9fafb" }} value={form.piva} onChange={e => upd("piva", e.target.value)} disabled={!isPlatformOwner}
                    title={isPlatformOwner ? "" : "Per cambiarla contatta l'assistenza Nexiora"} placeholder="IT00000000000" />
                : <button type="button" onClick={() => richiediPiva()} disabled={!canEdit}
                    style={{ ...inp, textAlign: "left", cursor: canEdit ? "pointer" : "default", color: "#7c3aed", fontWeight: 700, background: "#f5f3ff", borderColor: "#ddd6fe" }}>+ Inserisci la Partita IVA</button>}
              {form.piva && !isPlatformOwner && <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 3 }}>Per cambiarla contatta l'assistenza Nexiora.</div>}
            </div>
          </div>

          <div className="form-group">
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Indirizzo</label>
            <input style={inp} value={form.indirizzo} onChange={e => upd("indirizzo", e.target.value)} disabled={!canEdit} placeholder="Via Roma 1, 00100 Roma" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Telefono</label>
              <input style={inp} value={form.telefono} onChange={e => upd("telefono", e.target.value)} disabled={!canEdit} placeholder="+39 0000 000000" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Email</label>
              <input style={inp} value={form.email} onChange={e => upd("email", e.target.value)} disabled={!canEdit} placeholder="info@azienda.it" />
            </div>
          </div>
        </div>

        {/* Logo */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            🖼️ Logo aziendale
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
            Il logo apparirà automaticamente in tutti i PDF generati da Nexiora — Rapportini, Cronoprogramma, Giornale di Cantiere, Verbali.
          </p>
          {/* Anteprima logo attuale */}
          {form.logo && (
            <div style={{ marginBottom: 12, padding: 12, background: "#f9fafb", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 12, border: "1px solid #e5e7eb" }}>
              <img src={form.logo} alt="Logo" style={{ maxHeight: 60, maxWidth: 200, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
              {canEdit && (
                <button type="button" onClick={()=>upd("logo","")}
                  style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  × Rimuovi
                </button>
              )}
            </div>
          )}
          {canEdit && (
            <div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px",
                background: uploadingLogo ? "#e5e7eb" : "#7c3aed", color: uploadingLogo ? "#9ca3af" : "#fff",
                borderRadius: 8, cursor: uploadingLogo ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 700, fontFamily: "'Inter',sans-serif", marginBottom: 10 }}>
                {uploadingLogo ? "⏳ Caricamento in corso..." : "📁 Carica logo dal computer"}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: "none" }}
                  onChange={e => { if(e.target.files[0]) uploadLogo(e.target.files[0]); }}
                  disabled={uploadingLogo}/>
              </label>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>
                Formati: PNG, JPG, SVG, WebP. Dimensione consigliata: 400×120px.
              </div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Oppure inserisci URL immagine</label>
              <input style={inp} value={form.logo} onChange={e => upd("logo", e.target.value)} placeholder="https://esempio.com/logo.png" />
            </div>
          )}
        </div>

        {/* Personalizzazione documenti per il cliente */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            🎨 Personalizzazione Documenti per il Cliente
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Colore, posizione del logo e piè di pagina dei documenti che mandi ai clienti: offerte, DDT, rapportini e report di commessa. Puoi lasciare tutto com'è: senza configurare nulla qui, il documento si genera comunque, con i valori di Nexiora già pronti.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Colore principale documenti</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="color" value={form.coloreDocumenti || "#1e1b4b"} onChange={e => upd("coloreDocumenti", e.target.value)} disabled={!canEdit}
                  style={{ width: 44, height: 34, padding: 2, border: "1px solid #e5e7eb", borderRadius: 6, cursor: canEdit ? "pointer" : "default" }}/>
                <input style={{...inp, flex:1}} value={form.coloreDocumenti || "#1e1b4b"} onChange={e => upd("coloreDocumenti", e.target.value)} disabled={!canEdit} placeholder="#1e1b4b"/>
              </div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>Default: il blu notte di Nexiora, se non lo cambi.</div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Posizione logo nel documento</label>
              <select style={inp} value={form.posizioneLogo || "sinistra"} onChange={e => upd("posizioneLogo", e.target.value)} disabled={!canEdit}>
                <option value="sinistra">Sinistra (default)</option>
                <option value="centro">Centro</option>
                <option value="destra">Destra</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Piè di pagina personalizzato (opzionale)</label>
            <textarea style={{...inp, minHeight:70, resize:"vertical"}} value={form.piePagina || ""} onChange={e => upd("piePagina", e.target.value)} disabled={!canEdit}
              placeholder={"Es. Ragione Sociale Srl · Capitale sociale € 10.000 i.v. · REA MI-000000\nIscrizione Registro Imprese Milano n. 00000000000"}/>
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>Compare in fondo a offerte, DDT, rapportini e report di commessa — utile per dati legali come capitale sociale, REA, iscrizioni. Se lo lasci vuoto, non compare nulla in più.</div>
          </div>
        </div>
        </div>

        {/* Anteprima intestazione PDF — affiancata in tempo reale */}
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, position: "sticky", top: 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 4, color: "#374151" }}>
            🖨️ Anteprima intestazione documenti PDF
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
            Così apparirà l'intestazione in tutti i PDF generati da Nexiora — Report, DDT, Rapportini, Sopralluoghi, Richieste Commerciali.
          </p>
          <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
            {/* Preview intestazione PDF */}
            <div style={{ borderBottom: "3px solid #1e1b4b", paddingBottom: 12, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                {form.logo
                  ? <img src={form.logo} alt="Logo" style={{ height: 40, objectFit: "contain", marginBottom: 4 }}/>
                  : <div style={{ fontSize: 18, fontWeight: 900, color: "#1e1b4b" }}>◆ {form.nome || <span style={{ color: "#fca5a5" }}>Nome azienda mancante</span>}</div>
                }
                <div style={{ fontSize: 11, color: "#374151", fontWeight: 700, marginTop: 4 }}>{form.nome || <span style={{ color: "#dc2626", fontSize: 11 }}>⚠️ Inserisci la ragione sociale</span>}</div>
                {form.indirizzo && <div style={{ fontSize: 10, color: "#6b7280" }}>{form.indirizzo}</div>}
                <div style={{ fontSize: 10, color: "#6b7280" }}>
                  {form.piva ? `P.IVA ${form.piva}` : <span style={{ color: "#dc2626" }}>⚠️ P.IVA mancante</span>}
                  {form.codiceFiscale ? ` · CF ${form.codiceFiscale}` : ""}
                </div>
                {form.telefono && <div style={{ fontSize: 10, color: "#6b7280" }}>Tel. {form.telefono}{form.email ? ` · ${form.email}` : ""}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b", textTransform: "uppercase", letterSpacing: ".04em" }}>Documento</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#7c3aed", fontFamily: "monospace" }}>XXX-2026-0001</div>
                <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
              </div>
            </div>
            {(!form.nome || !form.piva) && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#dc2626", fontWeight: 600 }}>
                ⚠️ Completa i dati aziendali per un'intestazione professionale nei tuoi documenti.
              </div>
            )}
            {form.nome && form.piva && (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#065f46", fontWeight: 600 }}>
                ✅ Intestazione completa — apparirà così in tutti i PDF generati da Nexiora.
              </div>
            )}
          </div>
        </div>
        </div>

        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginTop:16 }}>
          <h4 style={{ fontSize:13, fontWeight:700, color:"#1e1b4b", marginBottom:4 }}>📍 Disponibilità Mezzi e Attrezzature</h4>
          <p style={{ fontSize:11.5, color:"#6b7280", marginBottom:10 }}>
            Modulo per coordinare furgoni, auto e attrezzature tra i colleghi — dove si trovano, chi le ha, richieste e risposte tra colleghi. Compare nell'area personale di tutti solo se attivo.
          </p>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor: canEdit?"pointer":"default" }}>
            <input type="checkbox" checked={!!form.disponibilitaMezziAbilitato} disabled={!canEdit}
              onChange={async e => {
                const nuovo = e.target.checked;
                upd("disponibilitaMezziAbilitato", nuovo);
                await updateDoc(doc(db,"organizations",effectiveOrgId), { disponibilitaMezziAbilitato: nuovo, updatedAt: serverTimestamp() });
                addToast(nuovo ? "Modulo attivato ✅" : "Modulo disattivato","success");
              }}/>
            <span style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Attiva per tutta l'organizzazione</span>
          </label>
        </div>

        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginTop:16 }}>
          <h4 style={{ fontSize:13, fontWeight:700, color:"#1e1b4b", marginBottom:4 }}>📄 Scadenze contrattuali del personale</h4>
          <p style={{ fontSize:11.5, color:"#6b7280", marginBottom:10 }}>
            Avvisa quando sta per scadere un periodo di prova (30 giorni prima) o un contratto a termine (60 giorni prima), e quando matura un anno di servizio — utile per verificare gli scatti di anzianità previsti dal CCNL. Usa le date già presenti nell'anagrafica dipendenti. Compare nello Scadenzario, scheda Personale, e solo a chi ha il permesso «Scadenze contrattuali del personale» (di default titolare e amministrazione).
          </p>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor: canEdit?"pointer":"default" }}>
            <input type="checkbox" checked={form.scadenzePersonaleAttive !== false} disabled={!canEdit}
              onChange={async e => {
                const nuovo = e.target.checked;
                upd("scadenzePersonaleAttive", nuovo);
                await updateDoc(doc(db,"organizations",effectiveOrgId), { scadenzePersonaleAttive: nuovo, updatedAt: serverTimestamp() });
                addToast(nuovo ? "Avvisi attivati ✅" : "Avvisi disattivati","success");
              }}/>
            <span style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Attiva per tutta l'organizzazione</span>
          </label>
        </div>

        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginTop:16 }}>
          <h4 style={{ fontSize:13, fontWeight:700, color:"#1e1b4b", marginBottom:4 }}>⚡ Impianti e verifiche normative</h4>
          <p style={{ fontSize:11.5, color:"#6b7280", marginBottom:10 }}>
            Per chi realizza o manutiene impianti presso i clienti: anagrafica degli impianti (cabine MT/BT, impianti di terra, quadri, colonnine di ricarica, fotovoltaico...) e scadenzario delle verifiche periodiche obbligatorie di legge, con calcolo automatico delle scadenze e tracciabilità delle modifiche.
            Attivandolo compare la voce «Impianti» nel menu e la scheda «Verifiche normative» nello Scadenzario.
            <strong> È spento di default</strong>: serve alle aziende di impiantistica, non a tutte.
          </p>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor: canEdit?"pointer":"default" }}>
            <input type="checkbox" checked={!!form.impiantiVerificheAttivi} disabled={!canEdit}
              onChange={async e => {
                const nuovo = e.target.checked;
                upd("impiantiVerificheAttivi", nuovo);
                await updateDoc(doc(db,"organizations",effectiveOrgId), { impiantiVerificheAttivi: nuovo, updatedAt: serverTimestamp() });
                addToast(nuovo ? "Modulo attivato ✅" : "Modulo disattivato","success");
              }}/>
            <span style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Attiva per tutta l'organizzazione</span>
          </label>
        </div>

        <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginBottom:16 }}>
          <h4 style={{ fontSize:13, fontWeight:700, color:"#1e1b4b", marginBottom:4 }}>📱 Promemoria manutenzione anche via WhatsApp</h4>
          <p style={{ fontSize:11.5, color:"#6b7280", marginBottom:10 }}>
            Oltre all'email già attiva, i promemoria sulle scadenze del capitolato di manutenzione possono arrivare anche su WhatsApp — solo a chi ha inserito il proprio numero e dato il consenso dalle proprie Impostazioni personali.
            <strong> Richiede un canale WhatsApp Business attivo lato Nexiora</strong>: finché non è configurato, questo interruttore non ha alcun effetto, anche se acceso — nessun costo, nessun rischio nel lasciarlo pronto in anticipo.
          </p>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor: canEdit?"pointer":"default" }}>
            <input type="checkbox" checked={!!form.promemoriaManutenzioneWhatsapp} disabled={!canEdit}
              onChange={async e => {
                const nuovo = e.target.checked;
                upd("promemoriaManutenzioneWhatsapp", nuovo);
                await updateDoc(doc(db,"organizations",effectiveOrgId), { promemoriaManutenzioneWhatsapp: nuovo, updatedAt: serverTimestamp() });
                addToast(nuovo ? "Canale abilitato per l'organizzazione ✅" : "Canale disabilitato","success");
              }}/>
            <span style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Abilita per questa organizzazione</span>
          </label>
        </div>

        <div style={{ marginTop:16 }}>
          <LogEmailWidget/>
        </div>
        </>)}

        {activeTab === "economico" && (<>
        <PromemoriaAltreSez tab="economico"/>
        <AvvisoRuoliOperativi/>
        {/* Fonte ore */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            <FileText size={14} color="#7c3aed" /> Controllo Economico — Fonte ore
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Scegli da dove il Controllo Economico legge le ore lavorate per il calcolo dei costi di manodopera.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
            {[
              { val: "programmazione", emoji: "📅", label: "Programmazione settimanale", desc: "Dalla pianificazione settimanale inserita dal titolare. Scegliendo questa, il campo ore compare in Programmazione Settimanale per ogni persona e commessa — con le altre due fonti resta nascosto, perché le ore arriverebbero da un altro posto." },
              { val: "rapportini", emoji: "📋", label: "Rapportini di intervento", desc: "Dai rapportini compilati dagli operai a fine giornata." },
              { val: "presenze", emoji: "🕐", label: "Presenze (timbratura)", desc: "Dalle timbrature reali entrata/uscita. Il dato più preciso." },
            ].map(opt => {
              const selezionata = form.fontOreEconomico === opt.val;
              return (
                <div key={opt.val} onClick={() => canEdit && upd("fontOreEconomico", opt.val)}
                  style={{ padding: 14, border: `2px solid ${selezionata ? "#7c3aed" : "#e5e7eb"}`, borderRadius: 12,
                    cursor: canEdit ? "pointer" : "default", background: selezionata ? "#faf5ff" : "#fff" }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{opt.emoji}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: selezionata ? "#5b21b6" : "#111827", marginBottom: 4 }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: selezionata ? "#7c3aed" : "#6b7280" }}>{opt.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Diario «Le mie ore» (W403): fonte per singola persona, per chi lavora su più
              commesse nella stessa giornata (tecnici d'ufficio, studi tecnici) */}
          <div style={{ background:"#faf5ff", color:"#5b21b6", border:"1px solid #ddd6fe", borderLeft:"4px solid #7c3aed", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:12, lineHeight:1.55 }}>
            <strong>⏱️ Personale Non operativo</strong> (ufficio, PM, direzione): segna le ore sulle commesse in <strong>«Le mie ore»</strong>, che compare da sola
            a chi in <strong>Dipendenti</strong> ha la scheda collegata al proprio account con Categoria operativa «Non operativo». Le sue ore sulle commesse arrivano solo da lì
            (mai due volte), al suo Costo/h, e il cartellino resta per presenze e paghe. Per gli Operativi vale la fonte scelta qui sopra.
          </div>

          {form.fontOreEconomico === "rapportini" && (
            <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
              <div style={{ fontSize:12.5, fontWeight:700, color:"#4c1d95", marginBottom:4 }}>Ore nei rapportini</div>
              <div style={{ fontSize:11.5, color:"#5b21b6", lineHeight:1.55, marginBottom:8 }}>
                Nei rapportini ogni operatore ha le sue <strong>ore lavorate</strong> (precompilate con la durata dell'intervento e modificabili): sono queste che diventano costo.
                Per i <strong>rapportini compilati prima</strong>, che avevano un solo orario per tutti, indica come vanno lette:
              </div>
              {[
                { val:"durata", label:"Ogni operatore ha lavorato per tutta la durata", esempio:"2 operatori, 08:00–16:00 → 8 ore a testa (16 ore in totale)" },
                { val:"totale", label:"La durata è il totale da dividere tra gli operatori", esempio:"2 operatori, 08:00–16:00 → 4 ore a testa (8 ore in totale)" },
              ].map(o => (
                <label key={o.val} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:"5px 0", cursor: canEdit?"pointer":"default", fontSize:12 }}>
                  <input type="radio" name="rapportiniOrePrecedenti" disabled={!canEdit} checked={(form.rapportiniOrePrecedenti||"durata")===o.val}
                    onChange={()=>upd("rapportiniOrePrecedenti", o.val)} style={{ marginTop:2, accentColor:"#7c3aed" }}/>
                  <span><strong>{o.label}</strong><br/><span style={{ color:"#6b7280" }}>{o.esempio}</span></span>
                </label>
              ))}
            </div>
          )}

          {!form.fontOreEconomico ? (
            <div style={{ background:"#fee2e2", border:"2px solid #dc2626", borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
              <span style={{ fontSize:20, flexShrink:0 }}>🔴</span>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#dc2626", marginBottom:4 }}>Azione richiesta — Fonte ore non impostata</div>
                <div style={{ fontSize:12, color:"#7f1d1d", lineHeight:1.6 }}>
                  Scegli una delle tre card qui sopra prima di iniziare a lavorare su Nexiora — questa scelta influenza tutti i conteggi del Controllo Economico.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderLeft:"4px solid #dc2626", borderRadius:8, padding:"12px 16px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#991b1b", marginBottom:4 }}>
                ⚠️ La scelta della fonte è definitiva per il calcolo economico
              </div>
              <div style={{ fontSize:11, color:"#7f1d1d", lineHeight:1.5 }}>
                Cambiarla dopo l'avvio dell'operatività azzera e ricalcola tutti i totali già accumulati. Il modulo Presenze resta comunque sempre attivo per il controllo orari/ritardi, qualunque fonte scegli.
                <br/><br/>
                Se prevedi di cambiare fonte in futuro, ti consigliamo di salvare prima un'<strong>istantanea dei valori attuali</strong> (ore totali e costo di ciascuna commessa) — trovi il tasto apposito nella sezione Controllo Economico di ogni commessa. In alternativa, puoi comunque annotare questi valori altrove (ad esempio in un foglio Excel) come promemoria.
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: canEdit ? "1fr 2fr" : "1fr", gap: 10 }}>
        {/* Costo orario manodopera */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            💰 Costo orario manodopera
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Usato per il costo manodopera nel Controllo Economico. Personalizzabile per singolo dipendente.
          </p>
          <div className="form-group" style={{ maxWidth: 200 }}>
            <label className="form-label">Costo orario base (€/ora)</label>
            <input className="input" type="number" min="0" step="0.50"
              value={form.costoOrarioBase ?? ""} placeholder="es. 21.00"
              onChange={e => upd("costoOrarioBase", e.target.value)} disabled={!canEdit}/>
          </div>
        </div>

        {canEdit && (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: "#374151" }}>
              💼 Parametri commerciali — Inoltro Costi
            </h3>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
              Valori di default per l'Inoltro Costi al Commerciale. Modificabili per ogni singola richiesta.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Markup/Utile default (%)</label>
                <input style={inp} type="number" min="0" max="999" step="0.1"
                  value={form.markupDefault ?? 15} onChange={e => upd("markupDefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 15"/>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Spese generali default (%)</label>
                <input style={inp} type="number" min="0" max="999" step="0.1"
                  value={form.costiGeneraliDefault ?? 10} onChange={e => upd("costiGeneraliDefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 10"/>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Costo €/km default</label>
                <input style={inp} type="number" min="0" step="0.01"
                  value={form.costoKmDefault ?? 0.30} onChange={e => upd("costoKmDefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 0.30"/>
              </div>
            </div>

            <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginTop: 22, marginBottom: 14, color: "#374151" }}>
              📐 Parametri commerciali — Elaborazione Offerte
            </h3>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
              Valori di default per l'Elaborazione Offerte. Tre margini indipendenti da quelli dell'Inoltro Costi qui sopra — modificabili per ogni singola elaborazione.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Utile default (%)</label>
                <input style={inp} type="number" min="0" max="999" step="0.1"
                  value={form.utileEODefault ?? 15} onChange={e => upd("utileEODefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 15"/>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Spese generali default (%)</label>
                <input style={inp} type="number" min="0" max="999" step="0.1"
                  value={form.speseGeneraliEODefault ?? 10} onChange={e => upd("speseGeneraliEODefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 10"/>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Utile su spese vive default (%)</label>
                <input style={inp} type="number" min="0" max="999" step="0.1"
                  value={form.utileSpeseViveEODefault ?? 5} onChange={e => upd("utileSpeseViveEODefault", parseFloat(e.target.value)||0)}
                  disabled={!canEdit} placeholder="Es. 5"/>
              </div>
            </div>
          </div>
        )}

        {canEdit && (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginTop: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
              🔢 Numerazione Commesse
            </h3>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
              Scegli il formato che usate per i codici commessa (es. "12-26"). Se impostato, Nexiora proporrà da sola il numero successivo per ogni cliente — sempre modificabile a mano.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { val: "progressivo-anno", label: "Progressivo-Anno", esempio: "12-26, 13-26, 14-26..." },
                { val: "anno-progressivo", label: "Anno-Progressivo", esempio: "26-12, 26-13, 26-14..." },
              ].map(opt => (
                <div key={opt.val} onClick={() => canEdit && upd("formatoNumerazioneCommesse", opt.val)}
                  style={{ padding: 12, border: `2px solid ${form.formatoNumerazioneCommesse===opt.val ? "#7c3aed" : "#e5e7eb"}`, borderRadius: 10,
                    cursor: canEdit ? "pointer" : "default", background: form.formatoNumerazioneCommesse===opt.val ? "#faf5ff" : "#fff" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{opt.esempio}</div>
                </div>
              ))}
            </div>
            {!form.formatoNumerazioneCommesse && (
              <div style={{ marginTop: 10, fontSize: 11, color: "#9ca3af" }}>
                Non ancora impostato — oggi il codice commessa va sempre scritto a mano.
              </div>
            )}
          </div>
        )}

        {canEdit && <ImpostazioniSicurezza orgId={effectiveOrgId} form={form} upd={upd} inp={inp}/>}
        </div>
        </>)}

        {activeTab === "presenze" && (<>
        <PromemoriaAltreSez tab="presenze"/>
        <AvvisoRuoliOperativi/>
        {/* Impostazioni Presenze */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            🕐 Presenze — Impostazioni
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Parametri usati per il calcolo del cartellino presenze.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Tolleranza ritardo (minuti)</label>
              <input className="input" type="number" min="0" value={form.tolleranzaRitardo ?? 10}
                onChange={e => upd("tolleranzaRitardo", e.target.value)} disabled={!canEdit}/>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Minuti oltre l'orario previsto prima che venga segnalato come ritardo.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Ore lavorative al giorno</label>
              <input className="input" type="number" min="1" max="12" value={form.oreLavorativeStandard ?? 8}
                onChange={e => upd("oreLavorativeStandard", parseInt(e.target.value)||8)} disabled={!canEdit}/>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Ore lavorative giornaliere standard (default: 8). Le ore oltre questa soglia nei giorni feriali sono considerate straordinarie.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Orario inizio previsto</label>
              <input className="input" type="time" value={form.orarioInizioPrevisto ?? "08:00"}
                onChange={e => upd("orarioInizioPrevisto", e.target.value)} disabled={!canEdit}/>
            </div>
            <div className="form-group">
              <label className="form-label">Inizio fascia notturna</label>
              <input className="input" type="time" value={form.oraInizioNotturno ?? "22:00"}
                onChange={e => upd("oraInizioNotturno", e.target.value)} disabled={!canEdit}/>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Usata per proporre la tariffa giusta sugli interventi di manutenzione. La legge (D.Lgs. 66/2003) impone solo che la fascia mezzanotte–5:00 sia sempre inclusa — i confini esatti dipendono dal CCNL, default 22:00–06:00.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Fine fascia notturna</label>
              <input className="input" type="time" value={form.oraFineNotturno ?? "06:00"}
                onChange={e => upd("oraFineNotturno", e.target.value)} disabled={!canEdit}/>
            </div>
          </div>
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#eff6ff", borderRadius: 8, fontSize: 11, color: "#1d4ed8" }}>
            ℹ️ Viene sempre detratta automaticamente 1 ora per la pausa pranzo da ogni giornata lavorativa.
          </div>
        </div>

        {/* W419: cartellino obbligatorio — utils/obbligoPresenze.js */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            🔔 Timbrature dimenticate
          </h3>
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, lineHeight: 1.5 }}>
            Cosa succede quando qualcuno dimentica di timbrare. Contano i giorni in cui la persona era in Programmazione su un lavoro; se quella settimana l'azienda non ha programmato nessuno, i giorni dal lunedì al venerdì. Ferie, permessi e malattie registrati sono già a posto; chi è escluso dalla timbratura non riceve nulla. Titolare e amministratori non vengono mai fermati.
          </p>
          <div role="radiogroup" aria-label="Timbrature dimenticate" style={{ display: "grid", gap: 8 }}>
            {LIVELLI_OBBLIGO.map(l => {
              const scelto = livelloObbligo(form) === l.id;
              return (
                <label key={l.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10, cursor: canEdit ? "pointer" : "default",
                  border: `1.5px solid ${scelto ? "#7c3aed" : "#e5e7eb"}`, background: scelto ? "#f5f3ff" : "#fff" }}>
                  <input type="radio" name="obbligoPresenze" checked={scelto} disabled={!canEdit} style={{ marginTop: 3 }}
                    onChange={async () => {
                      const prima = livelloObbligo(form);
                      // i giorni contano da quando l'obbligo è acceso: niente valanga di giorni vecchi
                      const extra = prima === "spento" && l.id !== "spento" ? { obbligoPresenzeDal: dataStr(new Date()) } : {};
                      setForm(f => ({ ...f, obbligoPresenze: l.id, ...extra }));
                      try {
                        await updateDoc(doc(db, "organizations", effectiveOrgId), { obbligoPresenze: l.id, ...extra, updatedAt: serverTimestamp() });
                        addToast(`Timbrature dimenticate: ${l.label} ✅`, "success");
                      } catch (e) { addToast(messaggioErrore(e), "error"); }
                    }}/>
                  <span>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1e1b4b" }}>{l.label}</span>
                    <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.45 }}>{l.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {livelloObbligo(form) !== "spento" && form.obbligoPresenzeDal && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: "#6b7280" }}>Attivo dal {new Date(form.obbligoPresenzeDal + "T00:00:00").toLocaleDateString("it-IT")}: i giorni precedenti non vengono chiesti.</div>
          )}
        </div>

        {/* Responsabili approvazione presenze */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151" }}>
            <Users size={14} color="#7c3aed" /> Responsabili approvazione presenze
          </h3>
          <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            Oltre a owner e admin, puoi autorizzare altri utenti ad approvare i cartellini presenze.
          </p>
          <ResponsabiliPresenze orgId={effectiveOrgId} canEdit={canEdit}/>
        </div>
        </>)}

        {activeTab === "ruoli" && (<>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1e1b4b", margin:0 }}>🗂️ Ruoli e cosa vedono di default</h3>
            <button onClick={stampaTabellaRuoli}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", border:"1px solid #7c3aed", borderRadius:7, background:"#faf5ff", color:"#7c3aed", fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0, fontFamily:"'Inter',sans-serif" }}>
              🖨️ Stampa / PDF
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
            Ogni persona che inviti riceve un ruolo — questa tabella mostra cosa vede subito, senza bisogno di configurare nulla. Restano sempre personalizzabili dopo, per ruolo o per singola persona, in Abilitazioni. Il Titolare vede sempre tutto, non compare qui.
          </p>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11.5 }}>
              <thead>
                <tr>
                  <th style={{ textAlign:"left", padding:"7px 10px", background:"#1e1b4b", color:"#fff", fontWeight:700, position:"sticky", left:0 }}>Sezione</th>
                  {RUOLI_OPERATIVI.map(r => (
                    <th key={r.id} style={{ textAlign:"center", padding:"7px 8px", background:"#1e1b4b", color:"#fff", fontWeight:700, whiteSpace:"nowrap" }} title={r.descrizione}>{r.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SEZIONI.map((s,i) => (
                  <tr key={s.id} style={{ background: i%2===0?"#fff":"#fafafa" }}>
                    <td style={{ padding:"6px 10px", fontWeight:600, color:"#374151", position:"sticky", left:0, background:"inherit" }} title={s.desc}>{s.label}</td>
                    {RUOLI_OPERATIVI.map(r => (
                      <td key={r.id} style={{ textAlign:"center", padding:"6px 8px" }}>
                        {PERMESSI_DEFAULT_PER_RUOLO[r.id]?.sezioni.includes(s.id) ? <span style={{color:"#059669",fontWeight:700}}>✅</span> : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize:11.5, color:"#9ca3af", marginTop:12 }}>
            "Richieste al magazzino" e "Modifica sezione Acquisti" — un tempo gestite qui con elenchi separati — ora vivono anche loro in questa tabella, come tutte le altre sezioni.
          </p>
        </div>
        </>)}

        {canEdit && activeTab !== "ruoli" && (
          <button onClick={salva} disabled={saving} className="btn btn-primary" style={{ fontSize: 13, padding: "10px 24px" }}>
            {saving ? "Salvataggio..." : "Salva impostazioni"}
          </button>
        )}
      </div>
    </div>
  );
}
